import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import {
  AlertTriangle,
  Trash2,
  Pencil,
  ShieldCheck,
  X,
  AlertCircle,
  ShieldAlert,
  CheckCircle2,
  CircleOff,
  Plus,
  Upload,
  Eye,
  FileText,
  Clock,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Loader2,
} from "lucide-react";
import {
  Modal,
  FormField,
  FormInput,
  FormSelect,
  FormTextarea,
  ModalFooterButtons,
} from "../components/Modal";
import { DataTable, cycleSortDir, type Column } from "../components/DataTable";
import { TableToolbar } from "../components/TableToolbar";
import { RowsPerCardSelect } from "../components/EntityCardGrid";
import { RBACAction } from "../components/RBACButton";
import { Tip } from "../components/ui/tooltip";
import { showToast } from "../components/ui/custom-toast";
import {
  getAPI,
  postAPI,
  putAPI,
  deleteAPI,
  uploadSemanticRulePack,
  publishSemanticRulePack,
  getSemanticRulePacks,
  deleteSemanticRulePack,
} from "../../api/frameworksApi";
import { API_URL } from "../../constants";
import { validateMaxLength, validateRequired } from "../../utils/validators";
import type {
  IntelligenceLayerId,
  OrgUnit,
  ParsedApiError,
  RadioTone,
  RuleParams,
  RuleSeverity,
  StatusFilter,
  UnpublishedRuleItem,
  UnpublishedRulePack,
  ValidationRule,
} from "./ValidationRules/types";
import {
  BADGE_BASE,
  CATEGORY_BADGE_BASE,
  CATEGORY_COLORS,
  DEFAULT_CATEGORY_COLOR,
  INTELLIGENCE_LAYERS,
  LAYER_META_BY_ID,
  LS_LAYER_KEY,
  LS_ORG_KEY,
  LS_STATUS_KEY,
  LS_UNPUBLISHED_KEY,
  RADIO_TONE,
} from "./ValidationRules/constants";
import {
  classifyRuleLayer,
  extractRulePackId,
  isParamsConfigured,
  normalizeIntelligence,
  packRuleCount,
  parseApiError,
  paramsEqual,
  parseYamlRules,
} from "./ValidationRules/utils";


// ─── Visual helpers ─────────────────────────────────────────────────────────


function SeverityBadge({ severity }: { severity: RuleSeverity }) {
  const isMandatory = severity === "MANDATORY";
  return (
    <span
      className={`${BADGE_BASE} ${
        isMandatory
          ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20"
          : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20"
      }`}
    >
      {isMandatory ? "Mandatory" : "Standard"}
    </span>
  );
}


function CategoryBadge({ category }: { category?: string }) {
  if (!category)
    return (
      <span className="text-[11px] text-[#94a3b8] dark:text-white/25">—</span>
    );
  const cls =
    CATEGORY_COLORS[category.toLowerCase()] ?? DEFAULT_CATEGORY_COLOR;
  return <span className={`${CATEGORY_BADGE_BASE} ${cls}`}>{category}</span>;
}

function StatusBadge({ enabled }: { enabled: boolean }) {
  return (
    <span
      className={`${BADGE_BASE} ${
        enabled
          ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20"
          : "bg-slate-100 text-slate-500 border-slate-200 dark:bg-white/[0.04] dark:text-white/40 dark:border-white/[0.08]"
      }`}
    >
      {enabled ? "Active" : "Inactive"}
    </span>
  );
}

/**
 * Renders a rule's semantic intelligence layer as an icon + short label. Rules
 * without a classified layer show a subtle dash.
 */
function LayerCell({ layer }: { layer?: IntelligenceLayerId }) {
  if (!layer) {
    return (
      <span className="text-[11px] text-[#94a3b8] dark:text-white/25">—</span>
    );
  }
  const meta = LAYER_META_BY_ID[layer];
  const Icon = meta.icon;
  return (
    <div className="flex items-center gap-1.5">
      <span
        className={`inline-flex size-6 items-center justify-center rounded-md ${meta.tone.iconBg}`}
      >
        <Icon className={`size-3.5 ${meta.tone.iconText}`} />
      </span>
      <span className="text-[11px] font-medium text-[#0f172a] dark:text-white/80 leading-tight">
        {meta.order} {meta.label}
      </span>
    </div>
  );
}

interface LayerCounts {
  byLayer: Record<IntelligenceLayerId, { total: number; active: number }>;
  allTotal: number;
  allActive: number;
}

/**
 * Left-hand vertical pipeline. First row is "All Layers" (shows every rule,
 * including generic/unclassified ones). Each subsequent row is one intelligence
 * layer with a circular colored icon connected by a dotted line to the next
 * layer's icon. Selected row gets a soft tinted background + a right chevron.
 *
 * The sidebar sticks to the top of the viewport and scrolls independently
 * when it exceeds the viewport height — the page itself does not scroll along.
 */
function LayerFilterSidebar({
  layerCounts,
  selectedLayer,
  onSelectLayer,
}: {
  layerCounts: LayerCounts;
  selectedLayer: IntelligenceLayerId | "ALL";
  onSelectLayer: (l: IntelligenceLayerId | "ALL") => void;
}) {
  const allTotal = layerCounts.allTotal;
  const allActive = layerCounts.allActive;
  const classifiedTotal = INTELLIGENCE_LAYERS.reduce(
    (sum, l) => sum + layerCounts.byLayer[l.id].total,
    0,
  );
  const genericTotal = Math.max(0, allTotal - classifiedTotal);
  const isAllSelected = selectedLayer === "ALL";

  return (
    <aside className="rounded-2xl border border-border bg-card p-3 lg:sticky lg:top-4 lg:self-start">
      <ul className="space-y-1">
        {/* All Layers — always visible; includes generic/unclassified rules */}
        <li>
          <button
            type="button"
            onClick={() => onSelectLayer("ALL")}
            className={`group relative w-full flex items-center gap-3 rounded-xl pl-2 pr-2.5 py-2 text-left transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/40 ${
              isAllSelected
                ? "bg-gradient-to-b from-indigo-50/70 to-transparent dark:from-indigo-500/[0.08] shadow-sm"
                : "hover:bg-slate-50/70 dark:hover:bg-white/[0.03]"
            }`}
            aria-pressed={isAllSelected}
            title={`All Layers — ${allTotal} rules, ${allActive} active`}
          >
            <span className="relative flex-shrink-0 flex flex-col items-center">
              <span className="relative z-10 inline-flex size-10 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 shadow-sm">
                <ShieldCheck className="size-5 text-white" />
              </span>
              <span
                className="absolute left-1/2 -translate-x-1/2 border-l-2 border-dotted border-slate-200 dark:border-white/10"
                style={{ top: "40px", height: "24px" }}
                aria-hidden
              />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-[11px] font-semibold tracking-wider text-indigo-600 dark:text-indigo-400">
                ALL
              </span>
              <span
                className={`block text-[13px] leading-tight truncate ${
                  isAllSelected
                    ? "font-semibold text-foreground"
                    : "font-medium text-foreground"
                }`}
              >
                All Layers
              </span>
              <span className="mt-0.5 flex items-center gap-1 text-[11px]">
                <span className="text-muted-foreground font-medium tabular-nums">
                  {allTotal} {allTotal === 1 ? "Rule" : "Rules"}
                </span>
                <span className="text-muted-foreground">·</span>
                <span className="font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
                  {allActive} Active
                </span>
                {genericTotal > 0 && (
                  <>
                    <span className="text-muted-foreground">·</span>
                    <span
                      className="font-medium tabular-nums text-muted-foreground"
                      title="Generic (unclassified) rules"
                    >
                      {genericTotal} Generic
                    </span>
                  </>
                )}
              </span>
            </span>
            {isAllSelected && (
              <ChevronRight className="flex-shrink-0 size-4 text-indigo-600 dark:text-indigo-400" />
            )}
          </button>
        </li>

        {INTELLIGENCE_LAYERS.map((layer, idx) => {
          const c = layerCounts.byLayer[layer.id];
          const isSelected = selectedLayer === layer.id;
          const Icon = layer.icon;
          const isLast = idx === INTELLIGENCE_LAYERS.length - 1;
          return (
            <li key={layer.id}>
              <button
                type="button"
                onClick={() =>
                  onSelectLayer(isSelected ? "ALL" : layer.id)
                }
                className={`group relative w-full flex items-center gap-3 rounded-xl pl-2 pr-2.5 py-2 text-left transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/40 ${
                  isSelected
                    ? `${layer.tone.selectedBg} shadow-sm`
                    : "hover:bg-slate-50/70 dark:hover:bg-white/[0.03]"
                }`}
                aria-pressed={isSelected}
                title={`${layer.label} — ${c.total} rules, ${c.active} active`}
              >
                {/* Icon column: circular icon + dotted connector down to next row */}
                <span className="relative flex-shrink-0 flex flex-col items-center">
                  <span
                    className={`relative z-10 inline-flex size-10 items-center justify-center rounded-full ${layer.tone.iconBg}`}
                  >
                    <Icon className={`size-5 ${layer.tone.iconText}`} />
                  </span>
                  {!isLast && (
                    <span
                      className="absolute left-1/2 -translate-x-1/2 border-l-2 border-dotted border-slate-200 dark:border-white/10"
                      style={{ top: "40px", height: "24px" }}
                      aria-hidden
                    />
                  )}
                </span>

                {/* Text column: order, label, counts */}
                <span className="flex-1 min-w-0">
                  <span
                    className={`block text-[11px] font-semibold tracking-wider ${layer.tone.activeBadgeText}`}
                  >
                    {layer.order}
                  </span>
                  <span
                    className={`block text-[13px] leading-tight truncate ${
                      isSelected
                        ? "font-semibold text-foreground"
                        : "font-medium text-foreground"
                    }`}
                  >
                    {layer.label}
                  </span>
                  <span className="mt-0.5 flex items-center gap-1 text-[11px]">
                    <span className="text-muted-foreground font-medium tabular-nums">
                      {c.total} {c.total === 1 ? "Rule" : "Rules"}
                    </span>
                    <span className="text-muted-foreground">·</span>
                    <span
                      className={`font-semibold tabular-nums ${layer.tone.activeBadgeText}`}
                    >
                      {c.active} Active
                    </span>
                  </span>
                </span>

                {isSelected && (
                  <ChevronRight
                    className={`flex-shrink-0 size-4 ${layer.tone.activeBadgeText}`}
                  />
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

/**
 * Summarises a rule's parameters for the table. Empty / unset values surface
 * a clear "Not set" affordance the user can click to open the editor.
 */
function ParamsSummary({
  rule,
  onConfigure,
}: {
  rule: ValidationRule;
  onConfigure: () => void;
}) {
  if (rule.kind === "CUSTOM") {
    return (
      <span className="text-[11px] italic text-[#94a3b8] dark:text-white/30">
        —
      </span>
    );
  }
  const p = (rule.params ?? {}) as any;
  const notSet = (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onConfigure();
      }}
      className="inline-flex items-center gap-1 text-[11px] font-semibold text-orange-700 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300 underline underline-offset-2 decoration-dotted"
    >
      <AlertCircle className="size-3" />
      Not set — configure
    </button>
  );

  switch (rule.validatorType) {
    case "synonym_contains": {
      const aliases: string[] = Array.isArray(p.aliases) ? p.aliases : [];
      if (aliases.length === 0) return notSet;
      const shown = aliases.slice(0, 3);
      const extra = aliases.length - shown.length;
      return (
        <div
          className="flex flex-wrap items-center gap-1"
          title={aliases.join(", ")}
        >
          {shown.map((a) => (
            <span
              key={a}
              className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:border-indigo-500/20"
            >
              {a}
            </span>
          ))}
          {extra > 0 && (
            <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-[#f1f5f9] dark:bg-white/[0.05] text-[#64748b] dark:text-white/45 border border-[#e2e8f0] dark:border-white/[0.07]">
              +{extra}
            </span>
          )}
        </div>
      );
    }

    case "regex_match": {
      const pattern = String(p.pattern ?? "").trim();
      if (!pattern) return notSet;
      return (
        <div className="flex items-center gap-1.5 min-w-0">
          <code
            className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-[#f1f5f9] dark:bg-white/[0.06] text-[#0f172a] dark:text-white/80 truncate max-w-[180px]"
            title={pattern}
          >
            {pattern}
          </code>
        </div>
      );
    }

    case "count_gte": {
      if (typeof p.min !== "number" || p.min <= 0) return notSet;
      return (
        <span className="text-[12px] font-mono text-[#0f172a] dark:text-white/80">
          ≥ {p.min}
        </span>
      );
    }

    case "text_consistency": {
      if (typeof p.min_match_ratio !== "number" || p.min_match_ratio <= 0)
        return notSet;
      return (
        <span className="text-[12px] font-mono text-[#0f172a] dark:text-white/80">
          ≥ {Math.round(p.min_match_ratio * 100)}%
        </span>
      );
    }

    case "structure_exists":
    case "date_present":
      return (
        <span className="text-[11px] italic text-[#94a3b8] dark:text-white/30">
          No parameters
        </span>
      );

    default:
      return (
        <span className="text-[11px] italic text-[#94a3b8] dark:text-white/30">
          —
        </span>
      );
  }
}

// ─── Radio card (used for severity + status selectors) ─────────────────────


function RadioCard({
  selected,
  onClick,
  icon,
  label,
  description,
  tone,
}: {
  selected: boolean;
  onClick: () => void;
  icon: ReactNode;
  label: string;
  description: string;
  tone: RadioTone;
}) {
  const palette = RADIO_TONE[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`relative text-left p-3 rounded-lg border transition-all flex items-start gap-2.5 ${
        selected
          ? `${palette.active} shadow-sm`
          : "border-[#e2e8f0] dark:border-white/[0.07] bg-[#f8fafc] dark:bg-white/[0.02] hover:border-[#cbd5e1] dark:hover:border-white/20"
      }`}
    >
      <span
        className={`mt-0.5 flex-shrink-0 ${selected ? palette.iconActive : palette.iconIdle}`}
      >
        {icon}
      </span>
      <span className="flex-1 min-w-0">
        <span
          className={`block text-[13px] font-semibold ${selected ? "text-[#0f172a] dark:text-white" : "text-[#475569] dark:text-white/70"}`}
        >
          {label}
        </span>
        <span className="block text-[11px] mt-0.5 text-[#64748b] dark:text-white/45 leading-snug">
          {description}
        </span>
      </span>
      <span
        className={`mt-0.5 size-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
          selected
            ? `${palette.iconActive.replace("text-", "border-")}`
            : "border-[#cbd5e1] dark:border-white/25"
        }`}
      >
        {selected && (
          <span
            className={`size-1.5 rounded-full ${palette.iconActive.replace("text-", "bg-")}`}
          />
        )}
      </span>
    </button>
  );
}

// ─── Aliases (chip) input ───────────────────────────────────────────────────

function AliasesInput({
  values,
  onChange,
  placeholder = "Add term and press Enter…",
}: {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const addDraft = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (values.includes(trimmed)) {
      setDraft("");
      return;
    }
    onChange([...values, trimmed]);
    setDraft("");
  };

  const removeAt = (idx: number) => {
    onChange(values.filter((_, i) => i !== idx));
  };

  const handleKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addDraft();
    } else if (e.key === "Backspace" && !draft && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 w-full min-h-[38px] bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-2 py-1.5 border border-[#e2e8f0] dark:border-white/[0.07] focus-within:border-indigo-500 dark:focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/10"
      onClick={() => inputRef.current?.focus()}
    >
      {values.map((v, i) => (
        <span
          key={v}
          className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:border-indigo-500/20"
        >
          {v}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              removeAt(i);
            }}
            className="text-indigo-500 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-200"
            aria-label={`Remove ${v}`}
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKey}
        onBlur={addDraft}
        placeholder={values.length === 0 ? placeholder : ""}
        className="flex-1 min-w-[120px] bg-transparent outline-none text-sm text-[#0f172a] dark:text-white placeholder:text-[#64748b] dark:placeholder:text-white/25"
      />
    </div>
  );
}

// ─── Typed params editor ────────────────────────────────────────────────────

/** Validators that have no editable parameters — the Parameters section is hidden. */
const NO_PARAM_VALIDATORS = new Set(["structure_exists", "date_present"]);

function ParamsEditor({
  validatorType,
  value,
  onChange,
}: {
  validatorType: string;
  value: RuleParams;
  onChange: (next: RuleParams) => void;
}) {
  switch (validatorType) {
    case "synonym_contains": {
      const aliases = Array.isArray((value as any)?.aliases)
        ? (value as any).aliases
        : [];
      return (
        <FormField
          label="Aliases"
          hint="Terms or phrases the validator will search for (case-insensitive). Press Enter to add."
        >
          <AliasesInput
            values={aliases}
            onChange={(next) => onChange({ aliases: next })}
          />
        </FormField>
      );
    }

    case "regex_match": {
      const pattern = String((value as any)?.pattern ?? "");
      const caseInsensitive = Boolean((value as any)?.case_insensitive);
      return (
        <div className="space-y-3">
          <FormField label="Pattern" hint="A valid regular expression.">
            <input
              type="text"
              value={pattern}
              onChange={(e) =>
                onChange({ ...(value as object), pattern: e.target.value })
              }
              placeholder="e.g. ^Section\\s+\\d+"
              spellCheck={false}
              className="w-full text-sm font-mono bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 text-[#0f172a] dark:text-white placeholder:text-[#64748b] dark:placeholder:text-white/25"
            />
          </FormField>
          <label className="flex items-center gap-2 text-sm text-[#475569] dark:text-white/70 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={caseInsensitive}
              onChange={(e) =>
                onChange({
                  ...(value as object),
                  case_insensitive: e.target.checked,
                })
              }
              className="size-4 rounded border-[#cbd5e1] dark:border-white/20 accent-indigo-600"
            />
            Case insensitive
          </label>
        </div>
      );
    }

    case "count_gte": {
      const min = (value as any)?.min ?? "";
      return (
        <FormField
          label="Minimum count"
          hint="The minimum number of occurrences required."
        >
          <input
            type="number"
            min={0}
            value={min === "" ? "" : Number(min)}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "")
                onChange({ ...(value as object), min: undefined as any });
              else onChange({ ...(value as object), min: Number(raw) });
            }}
            placeholder="e.g. 3"
            className="w-full text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 text-[#0f172a] dark:text-white placeholder:text-[#64748b] dark:placeholder:text-white/25"
          />
        </FormField>
      );
    }

    case "text_consistency": {
      const ratio = (value as any)?.min_match_ratio ?? "";
      return (
        <FormField
          label="Minimum match ratio"
          hint="A value between 0 and 1 (e.g. 0.6 means 60% of pages must match)."
        >
          <input
            type="number"
            min={0}
            max={1}
            step={0.05}
            value={ratio === "" ? "" : Number(ratio)}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "")
                onChange({
                  ...(value as object),
                  min_match_ratio: undefined as any,
                });
              else
                onChange({
                  ...(value as object),
                  min_match_ratio: Number(raw),
                });
            }}
            placeholder="0.0 – 1.0"
            className="w-full text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 text-[#0f172a] dark:text-white placeholder:text-[#64748b] dark:placeholder:text-white/25"
          />
        </FormField>
      );
    }

    default:
      return (
        <FormField
          label="Parameters (JSON)"
          hint="Unknown validator type — edit raw JSON."
        >
          <textarea
            value={JSON.stringify(value ?? {}, null, 2)}
            onChange={(e) => {
              try {
                onChange(JSON.parse(e.target.value || "{}"));
              } catch {
                // ignore parse errors while typing
              }
            }}
            rows={5}
            spellCheck={false}
            className="w-full text-sm font-mono bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 text-[#0f172a] dark:text-white"
          />
        </FormField>
      );
  }
}

// ─── Main component ─────────────────────────────────────────────────────────

export function ValidationRules() {
  // ── Orgs ────────────────────────────────────────────────────────────────
  const [orgs, setOrgs] = useState<OrgUnit[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [orgsError, setOrgsError] = useState<string | null>(null);

  const [selectedOrgId, setSelectedOrgId] = useState<string>(
    () => localStorage.getItem(LS_ORG_KEY) ?? "",
  );
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(
    () => (localStorage.getItem(LS_STATUS_KEY) as StatusFilter) ?? "ALL",
  );
  const [selectedLayer, setSelectedLayer] = useState<IntelligenceLayerId | "ALL">(
    () => {
      const raw = localStorage.getItem(LS_LAYER_KEY);
      if (raw === "ALL" || raw === null) return "ALL";
      return normalizeIntelligence(raw) ?? "ALL";
    },
  );

  // ── Rules ──────────────────────────────────────────────────────────────
  /* All rules for the selected org, fetched in one shot from
     GET /validation-rules?orgId=... The table, search, sort, and pagination
     all operate over this single in-memory list. */
  const [rules, setRules] = useState<ValidationRule[]>([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesError, setRulesError] = useState<string | null>(null);

  // ── Create modal ───────────────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const [createOrgId, setCreateOrgId] = useState<string>("");
  const [createTitle, setCreateTitle] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [createSeverity, setCreateSeverity] =
    useState<RuleSeverity>("MANDATORY");
  const [createAliases, setCreateAliases] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // ── Edit modal ─────────────────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<ValidationRule | null>(null);
  const [formParams, setFormParams] = useState<RuleParams>({});
  const [formSeverity, setFormSeverity] = useState<RuleSeverity>("MANDATORY");
  const [formEnabled, setFormEnabled] = useState<boolean>(true);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // ── Delete modal ───────────────────────────────────────────────────────
  const [showDelete, setShowDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ValidationRule | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // ── Table search + pagination (all client-side) ───────────────────────
  const [ruleSearch, setRuleSearch] = useState("");
  const [rulePage, setRulePage] = useState(0);
  const [rulePageSize, setRulePageSize] = useState(10);
  // Starts unsorted; a 3-state cycle (asc → desc → unsorted) restores the
  // original list order coming back from the server.
  const [ruleSortKey, setRuleSortKey] = useState<string | null>(null);
  const [ruleSortDir, setRuleSortDir] = useState<"asc" | "desc">("asc");
  const handleRuleSort = (key: string) => {
    const next = cycleSortDir(ruleSortKey, ruleSortDir, key);
    setRulePage(0);
    setRuleSortKey(next.sortKey);
    setRuleSortDir(next.sortDir);
  };

  // ── Unpublished Rule Packs State ───────────────────────────────────────
  const [unpublishedPacks, setUnpublishedPacks] = useState<
    UnpublishedRulePack[]
  >(() => {
    try {
      const stored = localStorage.getItem(LS_UNPUBLISHED_KEY);
      const packs: UnpublishedRulePack[] = stored ? JSON.parse(stored) : [];
      /* Packs saved before serverId existed kept the backend id in `id`.
         Carry it over, except for the locally generated keys - those were
         never real resources. */
      return packs.map((p) =>
        p.serverId || !p.id || /^(pack|local)-\d+$/.test(p.id)
          ? p
          : { ...p, serverId: p.id },
      );
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(LS_UNPUBLISHED_KEY, JSON.stringify(unpublishedPacks));
  }, [unpublishedPacks]);

  // ── Upload Modal & Unpublished List Modal States ───────────────────────
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showUploadedModal, setShowUploadedModal] = useState(false);
  const [activeUploadedPack, setActiveUploadedPack] =
    useState<UnpublishedRulePack | null>(null);

  const [showUnpublishedModal, setShowUnpublishedModal] = useState(false);
  const [unpublishedSearch, setUnpublishedSearch] = useState("");
  const [expandedPackId, setExpandedPackId] = useState<string | null>(null);
  const [publishingPackId, setPublishingPackId] = useState<string | null>(null);
  const [publishingAll, setPublishingAll] = useState(false);

  // Download Sample Rule Pack Template (.yml)
  const handleDownloadSampleTemplate = () => {
    const sampleYaml = `# Sample Semantic Rule Pack Template
packName: "Sample Security Rules"
packVersion: "1.0.0"
frameworkName: "NIST-SP-800-53"
rules:
  - id: "rule-sample-01"
    ruleCode: "SMP-001"
    title: "Enforce Multi-Factor Authentication"
    severity: "MANDATORY"
    category: "Authentication"
    remediation: "Configure MFA for all user logins in the identity provider."
    description: "Requires MFA to be enabled for all administrative and user access."

  - id: "rule-sample-02"
    ruleCode: "SMP-002"
    title: "Data Encryption at Rest"
    severity: "STANDARD"
    category: "Data Protection"
    remediation: "Enable AES-256 encryption on all storage volumes."
    description: "Ensures all sensitive storage volumes are encrypted at rest."
`;
    const blob = new Blob([sampleYaml], { type: "text/yaml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample-rule-pack.yml";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    // This branch's toast types are success | warning | critical | error.
    showToast("success", "Sample rule pack template downloaded.");
  };

  // Close upload pop modal and clean up draft
  const handleCloseUploadModal = () => {
    if (activeUploadedPack) {
      void handleDiscardUnpublishedPack(
        activeUploadedPack.id,
        activeUploadedPack.packName,
      );
      setActiveUploadedPack(null);
    }
    setShowUploadModal(false);
  };

  // ── YML Rule Pack Upload ──────────────────────────────────────────────
  const [uploadingYml, setUploadingYml] = useState(false);
  const ymlFileInputRef = useRef<HTMLInputElement>(null);

  const handleYmlFileSelect = async (file: File) => {
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith(".yml") && !fileName.endsWith(".yaml")) {
      showToast("error", "Only .yml and .yaml files are supported.");
      return;
    }

    setUploadingYml(true);
    try {
      let text = "";
      try {
        text = await file.text();
      } catch {
        text = "";
      }
      let parsed = parseYamlRules(text, file.name);

      let uploadRes: any = null;
      let uploadError: any = null;
      try {
        uploadRes = await uploadSemanticRulePack(file);
      } catch (err: any) {
        // If offline or dev mock, continue with parsed pack entry
        uploadError = err;
      }

      const resData = uploadRes?.data ?? uploadRes ?? {};
      const serverId = extractRulePackId(uploadRes);
      if (!serverId && uploadRes) {
        console.warn(
          "[ValidationRules] Rule pack upload returned no id:",
          uploadRes,
        );
      }
      const packId = serverId ?? `local-${Date.now()}`;
      const summary = resData?.summary ?? {};

      if (
        typeof resData.sourceContent === "string" &&
        resData.sourceContent.trim()
      ) {
        parsed = parseYamlRules(
          resData.sourceContent,
          resData.sourceFilename ?? file.name,
        );
      }
      const packName =
        resData.name ??
        resData.packName ??
        summary.name ??
        summary.packName ??
        parsed.packName;
      const rawRules = Array.isArray(resData.rules)
        ? resData.rules
        : Array.isArray(summary.rules)
          ? summary.rules
          : null;
      const serverRules: UnpublishedRuleItem[] = rawRules
        ? rawRules.map((r: any, idx: number) => ({
            id: String(r.id ?? `rule-${idx + 1}`),
            ruleCode: String(
              r.ruleCode ?? r.code ?? `R-${String(idx + 1).padStart(3, "0")}`,
            ),
            title: String(r.title ?? r.name ?? `Rule ${idx + 1}`),
            category: String(r.category ?? "semantic"),
            severity: (r.severity as RuleSeverity) ?? "MANDATORY",
            remediation: String(r.remediation ?? ""),
            description: String(r.description ?? ""),
          }))
        : [];

      const newPack: UnpublishedRulePack = {
        id: String(packId),
        serverId: serverId ?? undefined,
        fileName: resData.sourceFilename ?? file.name,
        packName: String(packName),
        uploadedAt:
          summary.uploadedAt ?? resData.createdAt ?? new Date().toISOString(),
        status: "UNPUBLISHED",
        rules: serverRules.length > 0 ? serverRules : parsed.rules,
        ruleCount:
          typeof summary.ruleCount === "number"
            ? summary.ruleCount
            : typeof resData.ruleCount === "number"
              ? resData.ruleCount
              : undefined,
        packVersion: summary.packVersion ?? resData.packVersion ?? undefined,
        frameworkName:
          summary.frameworkName ?? resData.frameworkName ?? undefined,
      };

      setUnpublishedPacks((prev) => [
        newPack,
        ...prev.filter((p) => p.id !== newPack.id),
      ]);

      setActiveUploadedPack(newPack);
      setShowUploadModal(true);

      if (serverId) {
        showToast(
          "success",
          `Uploaded "${file.name}" (${packRuleCount(newPack)} rules). Review preview and click Publish.`,
        );
      } else {
        showToast(
          "warning",
          uploadError?.message === "OFFLINE"
            ? `Parsed "${file.name}" locally - you appear to be offline, so it cannot be published yet.`
            : `Parsed "${file.name}", but the server did not return a rule pack id. Upload again before publishing.`,
        );
      }
    } catch (e: any) {
      showToast(
        "error",
        e?.message === "OFFLINE"
          ? "You appear to be offline."
          : e?.message || "Failed to upload semantic rule pack.",
      );
    } finally {
      setUploadingYml(false);
    }
  };

  const handlePublishPack = async (pack: UnpublishedRulePack) => {
    if (!pack.serverId) {
      showToast(
        "error",
        `"${pack.packName}" was never registered on the server. Upload the file again, then publish.`,
      );
      return;
    }

    setPublishingPackId(pack.id);
    try {
      await publishSemanticRulePack(pack.serverId);

      setUnpublishedPacks((prev) => prev.filter((p) => p.id !== pack.id));

      if (activeUploadedPack?.id === pack.id) {
        setShowUploadedModal(false);
        setActiveUploadedPack(null);
      }

      showToast(
        "success",
        `Rule pack "${pack.packName}" published successfully.`,
      );

      await fetchRules();
    } catch (e: any) {
      showToast(
        "error",
        e?.message === "OFFLINE"
          ? "You appear to be offline."
          : e?.message || "Failed to publish rule pack.",
      );
    } finally {
      setPublishingPackId(null);
    }
  };

  const handlePublishAllUnpublished = async () => {
    if (unpublishedPacks.length === 0) return;
    setPublishingAll(true);
    try {
      // Packs without a backend id cannot be published - they stay in the list.
      const publishedIds: string[] = [];
      const failed: string[] = [];
      const notRegistered = unpublishedPacks.filter((p) => !p.serverId);

      for (const pack of unpublishedPacks) {
        if (!pack.serverId) continue;
        try {
          await publishSemanticRulePack(pack.serverId);
          publishedIds.push(pack.id);
        } catch (e) {
          failed.push(pack.packName);
        }
      }

      setUnpublishedPacks((prev) =>
        prev.filter((p) => !publishedIds.includes(p.id)),
      );

      if (publishedIds.length > 0) {
        showToast(
          "success",
          `${publishedIds.length} unpublished rule pack(s) published successfully.`,
        );
      }
      if (failed.length > 0) {
        showToast("error", `Failed to publish: ${failed.join(", ")}.`);
      }
      if (notRegistered.length > 0) {
        showToast(
          "warning",
          `${notRegistered.length} pack(s) were never registered on the server. Upload them again, then publish.`,
        );
      }

      await fetchRules();
      if (publishedIds.length > 0 && failed.length === 0 && notRegistered.length === 0) {
        setShowUnpublishedModal(false);
      }
    } catch (e: any) {
      showToast("error", "Failed to publish all rule packs.");
    } finally {
      setPublishingAll(false);
    }
  };

  const handleDiscardUnpublishedPack = async (
    packId: string,
    packName: string,
  ) => {
    const serverId =
      unpublishedPacks.find((p) => p.id === packId)?.serverId ??
      (activeUploadedPack?.id === packId
        ? activeUploadedPack.serverId
        : undefined) ??
      null;

    setUnpublishedPacks((prev) => prev.filter((p) => p.id !== packId));
    if (activeUploadedPack?.id === packId) {
      setShowUploadedModal(false);
      setActiveUploadedPack(null);
    }

    if (!serverId) {
      // Never registered, so there is no uploaded file to remove.
      showToast("warning", `Unpublished rule pack "${packName}" discarded.`);
      return;
    }

    try {
      await deleteSemanticRulePack(serverId);
      showToast(
        "warning",
        `Unpublished rule pack "${packName}" discarded and its uploaded file deleted.`,
      );
    } catch (e: any) {
      showToast(
        "error",
        e?.message === "OFFLINE"
          ? `Removed "${packName}" from the list, but you appear to be offline - the uploaded file is still on the server.`
          : `Removed "${packName}" from the list, but its uploaded file could not be deleted. ${
              e?.message || "Try again from the unpublished list."
            }`,
      );
    }
  };

  const rulesFirstLoadDoneRef = useRef(false);


  // ── Fetch orgs once ────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setOrgsLoading(true);
      setOrgsError(null);
      try {
        const res = await getAPI(API_URL.ORG_UNITS + "?flat=true");
        if (cancelled) return;
        const raw: OrgUnit[] = res?.data ?? [];
        const list = raw.filter((o) => o.orgType === "ORGANIZATION");
        setOrgs(list);
        const stored = localStorage.getItem(LS_ORG_KEY);
        const initial =
          stored && list.some((o) => o.id === stored)
            ? stored
            : (list[0]?.id ?? "");
        setSelectedOrgId(initial);
      } catch (e: any) {
        if (cancelled) return;
        setOrgsError(
          e?.message === "OFFLINE"
            ? "You appear to be offline."
            : "Failed to load organizations.",
        );
      } finally {
        if (!cancelled) setOrgsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Fetch rules whenever org changes ───────────────────────────────────
  // Pulls the full list from GET /validation-rules?orgId=... in one call.
  // Search, sort, and pagination are all applied client-side over `rules`.
  const fetchRules = useCallback(async () => {
    if (!selectedOrgId) {
      setRules([]);
      return;
    }
    if (!rulesFirstLoadDoneRef.current) setRulesLoading(true);
    setRulesError(null);
    try {
      const params = new URLSearchParams({ orgId: selectedOrgId });
      const res = await getAPI(
        `${API_URL.VALIDATION_RULES}?${params.toString()}`,
      );
      const raw: ValidationRule[] = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.items)
          ? res.data.items
          : [];
      // Trust the layer the backend supplies; otherwise fall back to the
      // code/keyword classifier so "general" rules still land under a tile.
      setRules(
        raw.map((r) => ({
          ...r,
          intelligence:
            normalizeIntelligence(r.intelligence) ?? classifyRuleLayer(r),
        })),
      );
    } catch (e: any) {
      setRulesError(
        e?.message === "OFFLINE"
          ? "You appear to be offline."
          : "Failed to load validation rules.",
      );
      setRules([]);
    } finally {
      rulesFirstLoadDoneRef.current = true;
      setRulesLoading(false);
    }
  }, [selectedOrgId]);

  useEffect(() => {
    void fetchRules();
  }, [fetchRules]);

  useEffect(() => {
    if (selectedOrgId) localStorage.setItem(LS_ORG_KEY, selectedOrgId);
    localStorage.setItem(LS_STATUS_KEY, statusFilter);
    localStorage.setItem(LS_LAYER_KEY, selectedLayer);
  }, [selectedOrgId, statusFilter, selectedLayer]);

  // ── Open Create ────────────────────────────────────────────────────────
  const openCreate = () => {
    setCreateOrgId(selectedOrgId);
    setCreateTitle("");
    setCreateDescription("");
    setCreateSeverity("MANDATORY");
    setCreateAliases([]);
    setCreateError(null);
    setShowCreate(true);
  };

  const handleCreate = async () => {
    const title = createTitle.trim();
    const description = createDescription.trim();
    if (!createOrgId || !title || !description) {
      setCreateError("Organization, title, and description are required.");
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const payload: Record<string, any> = {
        orgId: createOrgId,
        kind: "CUSTOM",
        title,
        description,
        severity: createSeverity,
      };
      if (createAliases.length > 0) {
        payload.params = { aliases: createAliases };
      }
      await postAPI(API_URL.VALIDATION_RULES, payload);
      showToast("success", `"${title}" created.`);
      setShowCreate(false);
      if (createOrgId === selectedOrgId) {
        void fetchRules();
      } else {
        setSelectedOrgId(createOrgId);
      }
    } catch (e: any) {
      const parsed = parseApiError(e);
      setCreateError(parsed.banner);
    } finally {
      setCreating(false);
    }
  };

  // ── Open Edit ──────────────────────────────────────────────────────────
  const openEdit = (rule: ValidationRule) => {
    setEditTarget(rule);
    setFormParams(rule.params ?? {});
    setFormSeverity(rule.severity);
    setFormEnabled(rule.enabled);
    setFormError(null);
    setShowForm(true);
  };

  // ── Save (params / severity / status) ──────────────────────────────────
  const handleSave = async () => {
    if (!editTarget) return;
    setFormError(null);

    const body: Record<string, any> = {};
    const hasEditableParams =
      editTarget.kind !== "CUSTOM" &&
      !NO_PARAM_VALIDATORS.has(editTarget.validatorType);
    if (
      hasEditableParams &&
      !paramsEqual(formParams, editTarget.params ?? {})
    ) {
      body.params = formParams;
    }
    if (formSeverity !== editTarget.severity) body.severity = formSeverity;
    if (formEnabled !== editTarget.enabled) body.enabled = formEnabled;

    if (Object.keys(body).length === 0) {
      showToast("warning", "No changes to save.");
      setShowForm(false);
      return;
    }

    setFormSaving(true);
    try {
      await putAPI(`${API_URL.VALIDATION_RULES}/${editTarget.id}`, body);
      showToast("success", `"${editTarget.title}" updated.`);
      setShowForm(false);
      await fetchRules();
    } catch (e: any) {
      const parsed = parseApiError(e);
      setFormError(parsed.banner);
    } finally {
      setFormSaving(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────
  const openDelete = (rule: ValidationRule) => {
    setDeleteTarget(rule);
    setDeleteError(null);
    setShowDelete(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    const previous = rules;
    setRules((prev) => prev.filter((r) => r.id !== deleteTarget.id));
    try {
      await deleteAPI(`${API_URL.VALIDATION_RULES}/${deleteTarget.id}`);
      showToast("success", "Rule deleted.");
      setShowDelete(false);
      void fetchRules();
    } catch (e: any) {
      setRules(previous);
      const parsed = parseApiError(e);
      setDeleteError(parsed.banner);
    } finally {
      setDeleting(false);
    }
  };

  // Reset page when filters change
  useEffect(() => {
    setRulePage(0);
  }, [selectedOrgId, statusFilter, ruleSearch, rulePageSize, selectedLayer]);

  // Layer counts across the org's rules (independent of active layer selection,
  // but respecting status + search so the tiles react to those filters too).
  const layerCounts = useMemo(() => {
    const q = ruleSearch.trim().toLowerCase();
    const base: Record<
      IntelligenceLayerId,
      { total: number; active: number }
    > = {
      DOMAIN: { total: 0, active: 0 },
      REQUIREMENT: { total: 0, active: 0 },
      CONCEPT: { total: 0, active: 0 },
      RELATIONSHIP: { total: 0, active: 0 },
      CONSISTENCY: { total: 0, active: 0 },
      KNOWLEDGE: { total: 0, active: 0 },
      GOVERNANCE: { total: 0, active: 0 },
    };
    let allTotal = 0;
    let allActive = 0;
    for (const r of rules) {
      if (statusFilter === "ACTIVE" && !r.enabled) continue;
      if (statusFilter === "INACTIVE" && r.enabled) continue;
      if (q) {
        const match =
          (r.title ?? "").toLowerCase().includes(q) ||
          (r.ruleCode ?? "").toLowerCase().includes(q) ||
          (r.validatorType ?? "").toLowerCase().includes(q) ||
          (r.target ?? "").toLowerCase().includes(q) ||
          (r.category ?? "").toLowerCase().includes(q);
        if (!match) continue;
      }
      allTotal += 1;
      if (r.enabled) allActive += 1;
      const layer = r.intelligence as IntelligenceLayerId | undefined;
      if (layer && base[layer]) {
        base[layer].total += 1;
        if (r.enabled) base[layer].active += 1;
      }
    }
    return { byLayer: base, allTotal, allActive };
  }, [rules, ruleSearch, statusFilter]);

  // Filter (layer + status from `enabled`, plus free-text search)
  const filteredRules = useMemo(() => {
    const q = ruleSearch.trim().toLowerCase();
    return rules.filter((r) => {
      if (statusFilter === "ACTIVE" && !r.enabled) return false;
      if (statusFilter === "INACTIVE" && r.enabled) return false;
      if (selectedLayer !== "ALL" && r.intelligence !== selectedLayer)
        return false;
      if (!q) return true;
      return (
        (r.title ?? "").toLowerCase().includes(q) ||
        (r.ruleCode ?? "").toLowerCase().includes(q) ||
        (r.validatorType ?? "").toLowerCase().includes(q) ||
        (r.target ?? "").toLowerCase().includes(q) ||
        (r.category ?? "").toLowerCase().includes(q)
      );
    });
  }, [rules, ruleSearch, statusFilter, selectedLayer]);

  // Sort client-side. `null` sort key preserves the server's original order.
  const sortedRules = useMemo(() => {
    if (!ruleSortKey) return filteredRules;
    const key = ruleSortKey as keyof ValidationRule;
    const dir = ruleSortDir === "desc" ? -1 : 1;
    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: "base",
    });
    return [...filteredRules].sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      // enabled is boolean; treat true as coming before false in ascending.
      if (typeof av === "boolean" || typeof bv === "boolean") {
        return ((bv ? 1 : 0) - (av ? 1 : 0)) * dir;
      }
      const as = av == null ? "" : String(av);
      const bs = bv == null ? "" : String(bv);
      return collator.compare(as, bs) * dir;
    });
  }, [filteredRules, ruleSortKey, ruleSortDir]);

  // Paginate client-side and expose totalItems so the DataTable footer counts
  // over the filtered set (not the raw list).
  const ruleTotal = sortedRules.length;
  const paginatedRules = useMemo(
    () =>
      sortedRules.slice(
        rulePage * rulePageSize,
        rulePage * rulePageSize + rulePageSize,
      ),
    [sortedRules, rulePage, rulePageSize],
  );

  // Guard against pages that fall off the end (e.g. after a filter narrows
  // the list or a delete removes the last row of the current page).
  useEffect(() => {
    if (rulePage > 0 && rulePage * rulePageSize >= ruleTotal) {
      setRulePage(Math.max(0, Math.ceil(ruleTotal / rulePageSize) - 1));
    }
  }, [rulePage, rulePageSize, ruleTotal]);


  // ── Table columns ──────────────────────────────────────────────────────
  const columns: Column<ValidationRule>[] = useMemo(
    () => [
      {
        key: "sno",
        header: "#",
        align: "center",
        render: (_row, idx) => (
          <span className="text-xs font-medium text-muted-foreground">
            {rulePage * rulePageSize + idx + 1}
          </span>
        ),
      },
      {
        key: "ruleCode",
        header: "Code",
        align: "center",
        sortable: true,
        render: (r) => (
          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 text-[11px] font-semibold tracking-wide">
            {r.ruleCode}
          </span>
        ),
      },
      {
        key: "title",
        header: "Title",
        align: "left",
        sortable: true,
        ellipsis: 2,
        tooltip: (r) => r.title,
        render: (r) => (
          <div className="space-y-0.5">
            <p className="text-[13px] font-medium text-[#0f172a] dark:text-white/85">
              {r.title}
            </p>
            {r.remediation && (
              <p className="text-[11px] text-[#94a3b8] dark:text-white/35 line-clamp-1">
                {r.remediation}
              </p>
            )}
          </div>
        ),
      },
      {
        key: "intelligence",
        header: "Layer",
        align: "left",
        render: (r) => <LayerCell layer={r.intelligence} />,
      },
      {
        key: "params",
        header: "Parameters",
        align: "left",
        render: (r) => (
          <ParamsSummary rule={r} onConfigure={() => openEdit(r)} />
        ),
      },
      {
        key: "category",
        header: "Category",
        align: "center",
        sortable: true,
        cellClassName: "whitespace-nowrap!",
        render: (r) => <CategoryBadge category={r.category} />,
      },
      {
        key: "severity",
        header: "Severity",
        align: "center",
        sortable: true,
        cellClassName: "whitespace-nowrap!",
        render: (r) => <SeverityBadge severity={r.severity} />,
      },
      {
        key: "enabled",
        header: "Status",
        align: "center",
        sortable: true,
        cellClassName: "whitespace-nowrap!",
        render: (r) => <StatusBadge enabled={r.enabled} />,
      },
      {
        key: "actions",
        header: "Actions",
        align: "center",
        render: (r) => (
          <div className="flex items-center justify-center gap-1.5">
            <RBACAction module="validation_rule" action="update">
              <Tip label="Edit Rule">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openEdit(r);
                  }}
                  className="p-1.5 rounded-md text-[#475569] dark:text-white/50 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors"
                >
                  <Pencil className="size-3.5" />
                </button>
              </Tip>
            </RBACAction>
            <RBACAction module="validation_rule" action="delete">
              <Tip label="Delete Rule">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openDelete(r);
                  }}
                  className="p-1.5 rounded-md text-[#94a3b8] dark:text-white/30 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </Tip>
            </RBACAction>
          </div>
        ),
      },
    ],
    [rulePage, rulePageSize],
  );

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Validation Rules</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Per-organization rules used to validate uploaded documents. Only
            rules with status <span className="font-medium">ACTIVE</span> are
            enforced.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={ymlFileInputRef}
            type="file"
            accept=".yml,.yaml,application/x-yaml,text/yaml"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleYmlFileSelect(f);
              e.target.value = "";
            }}
          />

          {/* Upload Rule Pack Button opens Unified Upload & Rule Pack Manager Pop Modal */}
          <RBACAction module="validation_rule" action="create">
            <button
              type="button"
              onClick={() => setShowUploadModal(true)}
              disabled={uploadingYml}
              className="flex items-center gap-2 px-3.5 py-1.5 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-900 dark:text-white rounded-md border border-slate-300 dark:border-slate-700/50 text-sm font-medium transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
              title="Upload & Manage Rule Packs"
            >
              {uploadingYml ? (
                <Loader2 className="size-4 animate-spin text-indigo-600 dark:text-indigo-400" />
              ) : (
                <Upload className="size-4 text-indigo-600 dark:text-indigo-400" />
              )}
              <span>{uploadingYml ? "Uploading…" : "Upload Rule Pack"}</span>
            </button>
          </RBACAction>

          <RBACAction module="validation_rule" action="create">
            <button
              type="button"
              onClick={openCreate}
              disabled={orgsLoading || orgs.length === 0}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="size-4" />
              Add Rule
            </button>
          </RBACAction>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <TableToolbar
          className="flex-none w-full min-w-[220px] max-w-sm"
          search={{
            value: ruleSearch,
            onChange: setRuleSearch,
            placeholder: "Search by code, title, validator",
            ariaLabel: "Search validation rules",
          }}
        />
        <div className="flex flex-wrap items-center gap-3 ml-auto">
          {/* Status filter */}
          <div className="w-[160px] flex-shrink-0">
            <FormSelect
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as StatusFilter)}
              options={[
                { value: "ALL", label: "All status" },
                { value: "ACTIVE", label: "Active" },
                { value: "INACTIVE", label: "Inactive" },
              ]}
            />
          </div>

          {/* Rows per page */}
          <RowsPerCardSelect
            value={rulePageSize}
            onChange={(n) => {
              setRulePageSize(n);
              setRulePage(0);
            }}
            options={[10, 25, 50, 100]}
            label="Rows per page"
          />
        </div>
      </div>

      {/* Org-level error */}
      {orgsError && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-400">
          <AlertTriangle className="size-4 flex-shrink-0" />
          <p className="text-sm flex-1">{orgsError}</p>
        </div>
      )}

      {/* Body: Vertical layer pipeline (left) + Rules table (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-4 items-start">
        <LayerFilterSidebar
          layerCounts={layerCounts}
          selectedLayer={selectedLayer}
          onSelectLayer={setSelectedLayer}
        />

        <div className="min-w-0 space-y-3">
          {/* Rules table (client-paginated over `rules` fetched in one call) */}
          <DataTable
            columns={columns}
            data={paginatedRules}
            loading={rulesLoading}
            error={rulesError}
            rowKey={(r) => r.id}
            minTableWidth="880px"
            emptyText={
              !selectedOrgId
                ? "Select an organization to view its rules."
                : ruleSearch.trim()
                  ? "No rules match your search."
                  : selectedLayer !== "ALL"
                    ? `No rules in the ${LAYER_META_BY_ID[selectedLayer].label} layer.`
                    : "No validation rules for this organization."
            }
            emptyIcon={
              <ShieldCheck className="size-4 text-[#64748b] dark:text-white/40" />
            }
            page={rulePage}
            pageSize={rulePageSize}
            totalItems={ruleTotal}
            onPageChange={(p) => setRulePage(p)}
            itemLabel="rules"
            sortKey={ruleSortKey}
            sortDir={ruleSortDir}
            onSort={handleRuleSort}
          />
        </div>
      </div>

      {/* ──────────────────────────────────────────────────────────────────
          Upload Rule Pack Pop Modal
      ────────────────────────────────────────────────────────────────── */}
      <Modal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        title="Upload Semantic Rule Pack"
        description="Upload a YAML rule pack file (.yml or .yaml) to parse and register semantic validation rules."
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowUploadModal(false)}
            cancelText="Close"
          />
        }
      >
        <div className="space-y-5 py-2">
          {/* Drag & Drop / File Selector Area */}
          <div
            onClick={() => ymlFileInputRef.current?.click()}
            className="border-2 border-dashed border-indigo-500/30 hover:border-indigo-500 dark:border-indigo-500/20 dark:hover:border-indigo-400 bg-indigo-50/40 dark:bg-indigo-950/20 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 rounded-xl p-6 text-center cursor-pointer transition-all group"
          >
            <div className="mx-auto size-12 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400 mb-3 group-hover:scale-110 transition-transform">
              {uploadingYml ? (
                <Loader2 className="size-6 animate-spin" />
              ) : (
                <Upload className="size-6" />
              )}
            </div>
            <p className="text-sm font-semibold text-foreground">
              {uploadingYml ? "Uploading Rule Pack…" : "Click or Drag & Drop to Upload YAML File"}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Supports <span className="font-mono font-medium text-foreground">.yml</span> and <span className="font-mono font-medium text-foreground">.yaml</span> rule packs
            </p>
          </div>

          {/* Pop Modal Options */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            <button
              type="button"
              onClick={() => {
                setShowUploadModal(false);
                setShowUnpublishedModal(true);
              }}
              className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card hover:bg-accent transition-colors text-left group"
            >
              <div className="p-2 rounded-md bg-amber-50 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400 group-hover:scale-105 transition-transform">
                <Eye className="size-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-foreground flex items-center justify-between">
                  Unpublished Packs
                  {unpublishedPacks.length > 0 && (
                    <span className="px-1.5 py-0.5 text-[10px] font-bold text-amber-800 dark:text-amber-300 bg-amber-100 dark:bg-amber-500/20 rounded-full">
                      {unpublishedPacks.length}
                    </span>
                  )}
                </p>
                <p className="text-[11px] text-muted-foreground truncate">Review & publish draft packs</p>
              </div>
            </button>

            <button
              type="button"
              onClick={handleDownloadSampleTemplate}
              className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card hover:bg-accent transition-colors text-left group"
            >
              <div className="p-2 rounded-md bg-emerald-50 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 group-hover:scale-105 transition-transform">
                <FileText className="size-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-foreground">Sample Template</p>
                <p className="text-[11px] text-muted-foreground truncate">Download .yml sample</p>
              </div>
            </button>
          </div>
        </div>
      </Modal>

      {/* ──────────────────────────────────────────────────────────────────
          Create Modal — CUSTOM rules
      ────────────────────────────────────────────────────────────────── */}
      <Modal
        open={showCreate}
        onClose={() => !creating && setShowCreate(false)}
        title="Add Custom Validation Rule"
        description="Custom rules run alongside system rules for the selected organization."
        size="lg"
        closable={!creating}
        footer={
          <ModalFooterButtons
            onCancel={() => setShowCreate(false)}
            onSubmit={handleCreate}
            submitLabel={creating ? "Creating…" : "Create rule"}
            submitDisabled={creating}
          />
        }
      >
        <div className="space-y-4">
          {createError && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-400">
              <AlertTriangle className="size-4 flex-shrink-0 mt-0.5" />
              <p className="text-sm">{createError}</p>
            </div>
          )}

          <FormField label="Organization" required>
            <FormSelect
              value="ALL"
              onChange={() => {}}
              options={[{ value: "ALL", label: "All Organizations" }]}
              disabled
            />
          </FormField>

          <FormField label="Title" required>
            <FormInput
              value={createTitle}
              onChange={setCreateTitle}
              placeholder="e.g. Acceptance criteria present"
            />
          </FormField>

          <FormField label="Description" required>
            <FormTextarea
              value={createDescription}
              onChange={setCreateDescription}
              rows={4}
              placeholder="e.g. Verifies each user story includes a list of acceptance criteria."
            />
          </FormField>

          <div>
            <p className="text-sm font-semibold text-foreground mb-2">
              Parameters
            </p>
            <FormField
              label="Aliases"
              hint="Terms or phrases the validator will search for (case-insensitive). Press Enter to add."
            >
              <AliasesInput
                values={createAliases}
                onChange={setCreateAliases}
              />
            </FormField>
          </div>

          <FormField label="Severity" required>
            <div className="grid grid-cols-2 gap-2">
              <RadioCard
                selected={createSeverity === "MANDATORY"}
                onClick={() => setCreateSeverity("MANDATORY")}
                icon={<ShieldAlert className="size-4" />}
                label="Mandatory"
                description="Must pass for the document to be valid."
                tone="red"
              />
              <RadioCard
                selected={createSeverity === "STANDARD"}
                onClick={() => setCreateSeverity("STANDARD")}
                icon={<ShieldCheck className="size-4" />}
                label="Standard"
                description="Recommended check; failures are warnings."
                tone="amber"
              />
            </div>
          </FormField>
        </div>
      </Modal>

      {/* ──────────────────────────────────────────────────────────────────
          Edit Modal — SYSTEM rules: only params / severity / status
      ────────────────────────────────────────────────────────────────── */}
      <Modal
        open={showForm}
        onClose={() => !formSaving && setShowForm(false)}
        title={
          editTarget
            ? `${editTarget.ruleCode} — ${editTarget.title}`
            : "Edit Rule"
        }
        description="Adjust parameters, severity, or status. Other fields are managed by the system."
        size="lg"
        closable={!formSaving}
        footer={
          <ModalFooterButtons
            onCancel={() => setShowForm(false)}
            onSubmit={handleSave}
            submitLabel={formSaving ? "Saving…" : "Save changes"}
            submitDisabled={formSaving}
          />
        }
      >
        {editTarget && (
          <div className="space-y-4">
            {formError && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-400">
                <AlertTriangle className="size-4 flex-shrink-0 mt-0.5" />
                <p className="text-sm">{formError}</p>
              </div>
            )}

            {/* Read-only context */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <p className="text-[11px] font-medium tracking-wide text-[#94a3b8] dark:text-white/40">
                  Category
                </p>
                <CategoryBadge category={editTarget.category} />
              </div>
            </div>

            {editTarget.remediation && (
              <div className="space-y-1">
                <p className="text-[11px] font-medium tracking-wide text-[#94a3b8] dark:text-white/40">
                  Remediation
                </p>
                <p className="text-[13px] text-[#475569] dark:text-white/60 leading-relaxed">
                  {editTarget.remediation}
                </p>
              </div>
            )}

            <div className="border-t border-[#e2e8f0] dark:border-white/[0.06]" />

            {/* Editable: params (hidden for CUSTOM rules and validators with no params) */}
            {editTarget.kind !== "CUSTOM" &&
              !NO_PARAM_VALIDATORS.has(editTarget.validatorType) && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-foreground">
                      Parameters
                    </p>
                    {!isParamsConfigured(
                      editTarget.validatorType,
                      formParams,
                    ) && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-500/10 dark:text-orange-400 dark:border-orange-500/20">
                        <AlertCircle className="size-2.5" />
                        Needs setup
                      </span>
                    )}
                  </div>
                  <ParamsEditor
                    validatorType={editTarget.validatorType}
                    value={formParams}
                    onChange={setFormParams}
                  />
                </div>
              )}

            {/* Editable: severity (radio cards) */}
            <FormField label="Severity" required>
              <div className="grid grid-cols-2 gap-2">
                <RadioCard
                  selected={formSeverity === "MANDATORY"}
                  onClick={() => setFormSeverity("MANDATORY")}
                  icon={<ShieldAlert className="size-4" />}
                  label="Mandatory"
                  description="Must pass for the document to be valid."
                  tone="red"
                />
                <RadioCard
                  selected={formSeverity === "STANDARD"}
                  onClick={() => setFormSeverity("STANDARD")}
                  icon={<ShieldCheck className="size-4" />}
                  label="Standard"
                  description="Recommended check; failures are warnings."
                  tone="amber"
                />
              </div>
            </FormField>

            {/* Editable: status (driven by the `enabled` boolean) */}
            <FormField label="Status" required>
              <div className="grid grid-cols-2 gap-2">
                <RadioCard
                  selected={formEnabled}
                  onClick={() => setFormEnabled(true)}
                  icon={<CheckCircle2 className="size-4" />}
                  label="Active"
                  description="Enforced when validation runs."
                  tone="emerald"
                />
                <RadioCard
                  selected={!formEnabled}
                  onClick={() => setFormEnabled(false)}
                  icon={<CircleOff className="size-4" />}
                  label="Inactive"
                  description="Saved but not enforced."
                  tone="slate"
                />
              </div>
            </FormField>
          </div>
        )}
      </Modal>

      {/* ──────────────────────────────────────────────────────────────────
          Delete Confirmation Modal
      ────────────────────────────────────────────────────────────────── */}
      <Modal
        open={showDelete}
        onClose={() => !deleting && setShowDelete(false)}
        title={`Delete "${deleteTarget?.title}"?`}
        size="sm"
        closable={!deleting}
        footer={
          <div className="flex items-center justify-end gap-3 w-full">
            <button
              onClick={() => setShowDelete(false)}
              disabled={deleting}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 transition-colors disabled:opacity-40 shadow-md shadow-red-500/20"
            >
              {deleting ? "Deleting…" : "Delete"}
            </button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-[#475569] dark:text-white/60">
            This rule will no longer be enforced and will be removed from the
            list. This cannot be undone from the UI.
          </p>
          {deleteError && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-400 text-sm">
              <AlertTriangle className="size-4 flex-shrink-0 mt-0.5" />
              <p>{deleteError}</p>
            </div>
          )}
        </div>
      </Modal>



      {/* ──────────────────────────────────────────────────────────────────
          Upload Rule Pack Pop Modal (Upload, Sample Format, Preview & Publish)
      ────────────────────────────────────────────────────────────────── */}
      <Modal
        open={showUploadModal}
        onClose={handleCloseUploadModal}
        title={activeUploadedPack ? "Uploaded Rule Pack Preview" : "Upload Semantic Rule Pack"}
        description={
          activeUploadedPack
            ? `Review extracted rules from "${activeUploadedPack.fileName}" before publishing.`
            : "Upload a YAML rule pack file (.yml or .yaml) to parse and register semantic validation rules."
        }
        size="xl"
        wide
        footer={
          <div className="flex items-center justify-end w-full gap-2">
            <button
              type="button"
              onClick={handleCloseUploadModal}
              className="px-4 py-1.5 rounded-lg border border-border text-xs font-semibold text-foreground hover:bg-accent transition-colors"
            >
              Cancel
            </button>

            {activeUploadedPack && (
              <button
                type="button"
                onClick={() => handlePublishPack(activeUploadedPack)}
                disabled={
                  publishingPackId === activeUploadedPack.id ||
                  !activeUploadedPack.serverId
                }
                title={
                  activeUploadedPack.serverId
                    ? "Publish this rule pack"
                    : "This pack was not registered on the server. Upload the file again before publishing."
                }
                className="flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 transition-colors shadow-sm disabled:opacity-50 min-w-[120px]"
              >
                {publishingPackId === activeUploadedPack.id ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <CheckCircle2 className="size-3.5" />
                )}
                {publishingPackId === activeUploadedPack.id
                  ? "Publishing…"
                  : "Publish"}
              </button>
            )}
          </div>
        }
      >
        <div className="space-y-5">
          {/* Top Section: Shown ONLY when no file has been uploaded yet */}
          {!activeUploadedPack && (
            <>
              {/* Top Section (Up): Download Sample Format Card */}
              <div className="border border-emerald-500/20 bg-emerald-500/[0.04] dark:bg-emerald-950/20 rounded-xl p-3.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 flex-shrink-0">
                    <FileText className="size-5" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-foreground flex items-center gap-2">
                      YAML Rule Template Format
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/30">
                        Sample File
                      </span>
                    </h4>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Download a standard sample YAML rule pack template to structure your rules correctly before uploading.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleDownloadSampleTemplate}
                  className="flex items-center justify-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-colors shadow-sm whitespace-nowrap flex-shrink-0"
                >
                  <FileText className="size-3.5" />
                  Download Sample Format
                </button>
              </div>

              {/* Bottom Section (Down): Drag & Drop Upload Dropzone */}
              <div
                onClick={() => ymlFileInputRef.current?.click()}
                className="border-2 border-dashed border-indigo-500/30 hover:border-indigo-500 dark:border-indigo-500/20 dark:hover:border-indigo-400 bg-indigo-50/40 dark:bg-indigo-950/20 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 rounded-xl p-5 text-center cursor-pointer transition-all group flex flex-col items-center justify-center min-h-[120px]"
              >
                <div className="size-10 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400 mb-2 group-hover:scale-110 transition-transform">
                  {uploadingYml ? (
                    <Loader2 className="size-5 animate-spin" />
                  ) : (
                    <Upload className="size-5" />
                  )}
                </div>
                <p className="text-sm font-semibold text-foreground">
                  {uploadingYml
                    ? "Uploading Rule Pack…"
                    : "Click or Drag & Drop to Upload YAML File"}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Supports <code className="font-mono font-medium text-foreground">.yml</code> and <code className="font-mono font-medium text-foreground">.yaml</code> rule packs
                </p>
              </div>
            </>
          )}

          {/* Active Uploaded File Preview Section (Shown ONCE user has uploaded) */}
          {activeUploadedPack && (
            <div className="space-y-3">
              {/* Summary Header Card */}
              <div className="p-3.5 rounded-xl border border-indigo-100 dark:border-indigo-500/20 bg-indigo-50/40 dark:bg-indigo-500/[0.04] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 flex-shrink-0">
                    <FileText className="size-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-foreground flex items-center gap-2 flex-wrap">
                      {activeUploadedPack.packName}
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30 whitespace-nowrap">
                        Ready to Publish
                      </span>
                    </h4>
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-3 flex-wrap">
                      <span>
                        File:{" "}
                        <code className="font-mono">
                          {activeUploadedPack.fileName}
                        </code>
                      </span>
                      {activeUploadedPack.frameworkName && (
                        <>
                          <span>•</span>
                          <span>{activeUploadedPack.frameworkName}</span>
                        </>
                      )}
                      {activeUploadedPack.packVersion && (
                        <>
                          <span>•</span>
                          <span>v{activeUploadedPack.packVersion}</span>
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-card border border-border text-foreground whitespace-nowrap">
                    {packRuleCount(activeUploadedPack)} rule
                    {packRuleCount(activeUploadedPack) !== 1 ? "s" : ""} included
                  </span>
                </div>
              </div>

              {/* Extracted Rules Table Preview */}
              <div>
                <p className="text-xs font-semibold tracking-wider text-muted-foreground mb-2">
                  Extracted Rule Items ({activeUploadedPack.rules.length})
                </p>
                <div className="border border-border rounded-xl overflow-hidden bg-card">
                  <div className="overflow-x-auto max-h-[260px]">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead className="sticky top-0 z-20 bg-slate-900 text-slate-100 dark:bg-slate-950 dark:text-slate-100 border-b border-border shadow-sm">
                        <tr className="divide-x divide-white/10">
                          <th className="px-3.5 py-2 font-bold w-[60px] text-center whitespace-nowrap">#</th>
                          <th className="px-3.5 py-2 font-bold w-[130px] min-w-[130px] whitespace-nowrap">Code</th>
                          <th className="px-3.5 py-2 font-bold min-w-[200px]">Title & Remediation</th>
                          <th className="px-3.5 py-2 font-bold w-[110px] min-w-[110px] text-center whitespace-nowrap">Category</th>
                          <th className="px-3.5 py-2 font-bold w-[110px] min-w-[110px] text-center whitespace-nowrap">Severity</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border bg-card">
                        {activeUploadedPack.rules.map((rule, idx) => (
                          <tr
                            key={rule.id || idx}
                            className="divide-x divide-border hover:bg-accent/40 transition-colors"
                          >
                            <td className="px-3.5 py-2 text-center text-muted-foreground font-mono align-middle">
                              {idx + 1}
                            </td>
                            <td className="px-3.5 py-2 font-mono font-semibold text-indigo-600 dark:text-indigo-400 whitespace-nowrap align-middle">
                              {rule.ruleCode}
                            </td>
                            <td className="px-3.5 py-2 align-middle leading-relaxed">
                              <p className="font-semibold text-foreground">
                                {rule.title}
                              </p>
                              {(rule.remediation || rule.description) && (
                                <p className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5 font-normal">
                                  {rule.remediation || rule.description}
                                </p>
                              )}
                            </td>
                            <td className="px-3.5 py-2 text-center align-middle whitespace-nowrap">
                              <CategoryBadge category={rule.category || "semantic"} />
                            </td>
                            <td className="px-3.5 py-2 text-center align-middle whitespace-nowrap">
                              <SeverityBadge severity={rule.severity} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
